import { useEffect, useState } from 'react';
import {
  listActiveImageModels,
  listActiveVideoModels,
  listGenerationTemplatesForProject,
  listLanguages,
  listLLMProvidersPublic,
  listScriptSkillsForProject,
  listSkillsForProject,
  listVoicePresetsForProject,
} from '../../../api';
import {
  GenerationTemplate,
  LLMProvider,
  PromptSkill,
  ScriptSkill,
  VoicePreset,
  VideoModel,
  ImageModel,
} from '../../../types';
import { Language } from '../../../types/language';

/**
 * 全局参考列表（模型/技能/语言/音色/LLM）—— 全局静态、与项目数据无关。
 * 做会话级缓存（模块级 promise），多个调用方共享一次请求，避免重复打接口。
 * 注意：这是只读缓存，不写服务器，不影响多人协作；缓存的是"有哪些可选项"，
 * 不是项目自己的配置（那走 getProject，始终实时）。
 *
 * 生成模板（generationTemplates）**不缓存**：用户可能在会话内新建（TemplateList），
 * 协作者新建后需立即可见，故每次实时拉取。
 */
interface CachedLists {
  activeImageModels: ImageModel[];
  activeVideoModels: VideoModel[];
  languages: Language[];
  llmProviders: LLMProvider[];
  scriptSkills: ScriptSkill[];
  skills: PromptSkill[];
  voicePresets: VoicePreset[];
}

const EMPTY_LISTS: CachedLists = {
  activeImageModels: [],
  activeVideoModels: [],
  languages: [],
  llmProviders: [],
  scriptSkills: [],
  skills: [],
  voicePresets: [],
};

let cachedListsPromise: Promise<CachedLists> | null = null;

function loadCachedLists(): Promise<CachedLists> {
  if (!cachedListsPromise) {
    // 每个请求各自 catch → 单个失败不拖垮其余（与改造前逐个 catch 的容错一致）。
    cachedListsPromise = Promise.all([
      listActiveImageModels().catch(() => [] as ImageModel[]),
      listActiveVideoModels().catch(() => [] as VideoModel[]),
      listLanguages().catch(() => [] as Language[]),
      listLLMProvidersPublic().catch(() => [] as LLMProvider[]),
      listScriptSkillsForProject().catch(() => [] as ScriptSkill[]),
      listSkillsForProject().catch(() => [] as PromptSkill[]),
      listVoicePresetsForProject().catch(() => [] as VoicePreset[]),
    ]).then(([activeImageModels, activeVideoModels, languages, llmProviders, scriptSkills, skills, voicePresets]) => ({
      activeImageModels: activeImageModels || [],
      activeVideoModels: activeVideoModels || [],
      languages: languages || [],
      llmProviders: llmProviders || [],
      scriptSkills: scriptSkills || [],
      skills: skills || [],
      voicePresets: voicePresets || [],
    }));
  }
  return cachedListsPromise;
}

/** 供 EpisodeDetail 等场景复用缓存里的视频模型列表，避免重复请求。 */
export function loadActiveVideoModels(): Promise<VideoModel[]> {
  return loadCachedLists().then(d => d.activeVideoModels);
}

export function useProjectBootstrapOptions() {
  const [lists, setLists] = useState<CachedLists>(EMPTY_LISTS);
  const [generationTemplates, setGenerationTemplates] = useState<GenerationTemplate[]>([]);

  useEffect(() => {
    let alive = true;
    loadCachedLists().then(data => { if (alive) setLists(data); });
    // 模板实时拉取（不缓存）
    listGenerationTemplatesForProject().then(data => { if (alive) setGenerationTemplates(data || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return {
    activeImageModels: lists.activeImageModels,
    activeVideoModels: lists.activeVideoModels,
    generationTemplates,
    languages: lists.languages,
    llmProviders: lists.llmProviders,
    scriptSkills: lists.scriptSkills,
    skills: lists.skills,
    voicePresets: lists.voicePresets,
  };
}
